import {ReactNode, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {Input, message} from "antd";
import {
    ButtonCancel,
    ButtonSubmit,
    getChatMessages,
    getFileView,
    hexToRgbA,
    sleep,
    T,
    uploadBigFile
} from "./connector";
import {convertBEToMessage, convertMediaUrlsToFiles, imageUrlToBlobUrl, sentChatMessage} from "./tencent";
import TencentCloudChat, {ChatSDK} from "@tencentcloud/chat";
import Messages from "./messages";
import produce, {current} from "immer";
import {BEMessageType} from "./type";
import {TUICallType} from "tuicall-engine-webrtc";
import {IconAudioCall, IconVideoCall} from "./style";

interface ChatProps {
    mode?: 'customer' | 'admin',
    bookingId: string;
    tencentGroupId: string;
    tencentUserId?: string;
    chatSDK?: ChatSDK;
    header?: ReactNode,
    onMessage?: (eventName: "END-CHAT" | "MESSAGE", message: BEMessageType) => void;
    onMyselfMessage?: (eventName: "END-CHAT" | "MESSAGE", message: BEMessageType) => void;
    isEndedChat?: boolean;
    callInvited?: any;
    name?: string;
    threadId?: string;
    onAcceptInviteCall?: () => void;
}

export default function Chat(props: ChatProps) {
    const [loading, setLoading] = useState<boolean | 'sending' | 'history'>(false);
    const [text, setText] = useState<string>("");
    const [messages, setMessages] = useState<Array<any>>([]);
    const [paging, setPaging] = useState<any>({limit: 20, page: 1, isEnd: false, loading: false});
    const currentRef = useRef({
        tencentUserId: props?.tencentUserId,
        chatSDK: props.chatSDK,
        tencentGroupId: props.tencentGroupId,
        bookingId: props.bookingId,
        threadId: props?.threadId,
        name: props?.name,
    });
    const messagesRef = useRef<any>(null);
    const delaySendRef = useRef<any>(0);
    const [blobs, setBlobs] = useState<any>({});
    const blobsRef = useRef<any>({});

    useEffect(() => {
        // Load image blob url bypassing url signature
        messages.map(message => {
            if (message.type === 'image') {
                (async () => {
                    const file = message?.files?.[0];
                    const urlImage = file?.fileView || file?.file;
                    const urlImageKey = (urlImage || "").split("?")[0];
                    let blobUrl = null;
                    const requestThreadId = currentRef.current?.threadId?.toString() + "";

                    if (!blobsRef?.current[urlImageKey]?.loading) {
                        try {
                            if (!!blobsRef?.current[urlImageKey]?.result) {
                                setBlobs(produce(draft => {
                                    draft[urlImageKey] = blobsRef?.current[urlImageKey]?.result;
                                }))
                            } else {
                                blobsRef.current[urlImageKey] = {
                                    loading: true,
                                    result: null,
                                };
                                if (!urlImage?.includes('AWSAccessKeyId')) {
                                    const result = await getFileView(urlImageKey);
                                    if (requestThreadId !== currentRef.current?.threadId?.toString()) {
                                        blobsRef.current[urlImageKey] = null;
                                        return;
                                    }
                                    blobUrl = await imageUrlToBlobUrl(result?.data?.data);
                                    if (requestThreadId !== currentRef.current?.threadId?.toString()) {
                                        blobsRef.current[urlImageKey] = null;
                                        return;
                                    }
                                } else {
                                    blobUrl = await imageUrlToBlobUrl(urlImage);
                                    if (requestThreadId !== currentRef.current?.threadId?.toString()) {
                                        blobsRef.current[urlImageKey] = null;
                                        return;
                                    }
                                }
                                blobsRef.current[urlImageKey] = {
                                    loading: false,
                                    result: !!blobUrl ? blobUrl : null
                                };
                                setBlobs(produce(draft => {
                                    draft[urlImageKey] = !!blobUrl ? blobUrl : null;
                                }))
                            }

                        } catch (e) {
                            blobsRef.current[urlImageKey] = {
                                loading: false,
                                result: null
                            };
                        }
                    }
                })();
            }
        })
    }, [messages]);

    useEffect(() => {
        if (props?.tencentGroupId !== currentRef.current?.tencentGroupId) {
            // Reset state
            setMessages([]);
            setPaging({limit: 20, page: 1, isEnd: false, loading: true});
            setText("");
            blobsRef.current = {};
            delaySendRef.current = 0;
            try {
                // clean blobs
                Object.keys(blobs).map(key => {
                    if (blobs[key]) {
                        URL.revokeObjectURL(blobs[key]);
                    }
                });
            } catch (e) {
            }
            setBlobs({});
        }
        currentRef.current = {
            tencentUserId: props?.tencentUserId,
            chatSDK: props.chatSDK,
            tencentGroupId: props.tencentGroupId,
            bookingId: props.bookingId,
            threadId: props?.threadId,
            name: props?.name,
        };
        loadMessages().then();
    }, [props.tencentGroupId, paging?.page]);

    useEffect(() => {
        if (!!props?.chatSDK) {
            props?.chatSDK.on(TencentCloudChat.EVENT.MESSAGE_RECEIVED, event => {
                // event.name - TencentCloudChat.EVENT.MESSAGE_RECEIVED
                // event.data - An array to store Messages - [Message]
                event?.data?.map((item: any) => {
                    try {
                        if (!item.payload?.text) {
                            // ignore if not text message
                            return;
                        }
                        const beMessage = {
                            id: item.ID,
                            ...(item.payload?.text ? JSON.parse(item.payload?.text) : {}),
                        }
                        console.log("Receive message:", beMessage);
                        if (props?.onMessage) {
                            props?.onMessage(beMessage.eventName, beMessage);
                            // if MATCH-BOOKING upgrade to new booking
                            if (beMessage.eventName === "MATCH-BOOKING") {
                                currentRef.current.bookingId = beMessage.bookingId || currentRef.current.bookingId;
                                currentRef.current.tencentGroupId = beMessage.tencentGroupId || currentRef.current.tencentGroupId;
                                currentRef.current.threadId = beMessage.threadId || currentRef.current.threadId;
                            }
                        }
                        // Check end chat message
                        if (beMessage.eventName === "END-CHAT") {
                            return;
                        }
                        // Check message to group
                        if (item.to !== currentRef.current.tencentGroupId) {
                            return;
                        }

                        const message = convertBEToMessage(currentRef.current.tencentUserId, beMessage, props?.mode);
                        // check have message in local
                        setMessages(produce(draft => {
                            const items = current(draft);
                            const index = items.findIndex((item: any) => item.id === message.id);
                            if (index === -1) {
                                draft.unshift(message);
                            } else {
                                draft[index] = message;
                            }
                        }));

                    } catch (e) {
                        console.error("Error when receive message:", e);
                    }
                });
            });
        }
    }, [!!props?.chatSDK]);

    const loadMessages = async () => {
        if (paging.page === 1) {
            setLoading('history');
        }
        const requestThreadId = currentRef.current?.threadId?.toString() + "";
        const result = await getChatMessages(props?.bookingId, {
            page: paging.page,
            limit: paging.limit,
            tencentGroupId: props?.tencentGroupId,
            tencentUserId: props?.tencentUserId,
            bookingId: props?.bookingId,
            threadId: props?.threadId,
            name: props?.name,
        });
        if (requestThreadId !== currentRef.current?.threadId?.toString()) {
            return;
        }
        if (paging.page === 1) {
            setLoading(false);
        }
        if (result?.status === 200) {
            if (paging.page === 1) {
                const items = result?.data?.data?.items?.map((item: BEMessageType) => {
                    return convertBEToMessage(currentRef.current?.tencentUserId, item, props?.mode);
                });
                setMessages(items);
                setPaging(produce((draft: any) => {
                    draft.loading = false;
                    draft.isEnd = items.length < paging.limit;
                }));
                // Auto scroll to bottom smoothly
                try {
                    messagesRef.current.scrollTop = 0;
                    sleep(300).then(() => {
                        messagesRef.current.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        });
                    })
                } catch (e) {
                }
            } else {
                const items = result?.data?.data?.items?.map((item: BEMessageType) => {
                    return convertBEToMessage(currentRef.current?.tencentUserId, item, props?.mode);
                });
                setMessages(produce(draft => {
                    const newItems = items;
                    draft.push(...newItems);
                }));
                setPaging(produce((draft: any) => {
                    draft.loading = false;
                    draft.isEnd = items.length < paging.limit;
                }));
            }
        }
    }


    const onSend = async (text: string, files?: Array<File>) => {
        // Ignore if fast click
        if (Date.now() - delaySendRef.current < 500) {
            return;
        }
        delaySendRef.current = Date.now();
        text = (text || "")?.trim();
        if (!text && !files?.length) {
            message.error(T("Please enter your message or select a file!"));
            return;
        }
        if (text?.length > 2048) {
            message.error(T("Message is too long!"));
            return;
        }
        setLoading('sending');
        if (!!text) {
            setText("");
        }
        // Auto scroll to bottom smoothly
        sleep(100).then(() => {
            messagesRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        })
        // Add message to local
        const draftMessage = convertBEToMessage(currentRef.current.tencentUserId, {
            id: Date.now().toString(),
            contents: text,
            mediaUrls: (files || []).map(i => `${i.size}/${i.name}`),
            mediaBlobUrls: (files || []).map(file => {
                // convert image to blob url
                if (file.type?.includes("image")) {
                    const blob = new Blob([file], {type: file.type});
                    return URL.createObjectURL(blob);
                }
                return null;
            }).filter(blob => !!blob),
            tencentUserId: currentRef.current.tencentUserId,
            bookingId: currentRef.current.bookingId,
            tencentGroupId: currentRef.current.tencentGroupId,
            status: "sending",
            name: props?.name,
            threadId: currentRef.current.threadId,
            type: props.mode,
        }, props?.mode);
        setMessages(produce(draft => {
            draft.unshift(draftMessage);
        }));

        // If file is selected, upload it
        let fileUrls: Array<{
            fileUri: string;
            fileUriView: string;
        }> = [];
        await Promise.all((files || []).map(async (file: any) => {
            const result = await uploadBigFile(file, "chats");
            if (!!result?.fileUri) {
                fileUrls.push({
                    fileUri: (result?.fileUri || "")?.split("?")[0],
                    fileUriView: result?.fileUriView,
                });
            }
        }));
        const beMessage = {
            contents: text,
            bookingId: props?.bookingId,
            tencentGroupId: currentRef.current.tencentGroupId,
            tencentUserId: currentRef.current.tencentUserId,
            mediaUrls: fileUrls.map(item => item.fileUri),
            mediaCaption: text,
            name: props?.name,
            threadId: currentRef.current.threadId,
            type: props.mode,
        }
        if (props.onMyselfMessage) {
            props.onMyselfMessage("MESSAGE", {
                ...beMessage,
                eventName: "MESSAGE",
            });
        }
        const result = await sentChatMessage(currentRef.current.chatSDK, currentRef.current.tencentGroupId, beMessage);
        setLoading(false);
        if (result?.status === 200) {
            // Update status to sent
            setMessages(produce(draft => {
                const index = draft.findIndex((item: any) => item.id === draftMessage.id);
                if (index !== -1) {
                    draft[index].status = "active";
                    draft[index].id = result?.data?.data?.id;
                    draft[index].files = convertMediaUrlsToFiles(fileUrls.map(i => i.fileUriView) || result?.data?.data?.mediaUrls || []);
                }
            }));
        } else {
            // Show error message log
            console.log("Error when send message:", result);
            // Update status to error
            setMessages(produce(draft => {
                const index = draft.findIndex((item: any) => item.id === draftMessage.id);
                if (index !== -1) {
                    draft[index].status = "error";
                }
            }));
        }

    }

    const onSelectFile = async (e: any) => {
        if (e.target.files?.length) {
            const items = [];
            for (let i = 0; i < e.target.files.length; i++) {
                items.push(e.target.files[i]);
            }
            onSend("", items).then();
            await sleep(200);
            // @ts-ignore
            document.querySelector('#fileInput').value = "";
        }
    }
    const isNoMessages = !(messages || []).filter(message => !(!message?.text && !message?.files?.length)).length;
    return (
        <Container className={`chat-container ${props?.mode}`}>
            {props?.header}
            <Body>
                {
                    (loading === 'history') ? (
                        <Messages messagesRef={messagesRef} messages={[
                            {status: 'loading', type: "text", mode: "sent"},
                            {status: 'loading', type: "text", mode: "received"},
                            {status: 'loading', type: "text", mode: "sent"},
                            {status: 'loading', type: "text", mode: "received"},
                            {status: 'loading', type: "text", mode: "sent"},
                            {status: 'loading', type: "text", mode: "received"},
                        ]} paging={null} setPaging={() => {
                        }}/>
                    ) : (
                        <Messages callInvited={props?.callInvited} blobs={blobs} messagesRef={messagesRef}
                                  setMessages={setMessages} messages={messages}
                                  paging={paging}
                                  setPaging={setPaging}/>
                    )
                }
                {
                    isNoMessages && loading === false ? (
                        <NoMessages>
                            <NoMessagesIcon style={{width: 218, height: 169}}/>
                            {
                                props?.mode === 'customer' ? (
                                    <div>{T("Hello there,\n" +
                                        "How could we help you today?")}</div>
                                ) : (
                                    <div>{T("No inbox")}</div>
                                )
                            }
                        </NoMessages>
                    ) : null
                }
            </Body>
            {
                !props?.isEndedChat ? (
                    <Footer>
                        {props?.callInvited && (
                            <CallInviteButton onClick={props.onAcceptInviteCall}>
                                {props?.callInvited?.callType === TUICallType.VIDEO_CALL ? <IconVideoCall/> :
                                    <IconAudioCall/>}
                                {T("Join Call")}
                            </CallInviteButton>
                        )}


                        <FooterInput maxLength={2048} value={text}
                                     onChange={e => setText((e?.target?.value || ""))}
                                     onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                                         if (e.key === 'Enter' && !e.shiftKey) {
                                             e.preventDefault();
                                             onSend(text)
                                         }
                                     }}
                                     autoSize={{minRows: 1, maxRows: 4}} placeholder={T("Type your message here...")}/>
                        <FooterButtons>
                            <FooterButton htmlType={'button'} onClick={() => {
                                // @ts-ignore
                                document.querySelector('#fileInput')?.click();
                            }}>
                                <IconFile/>
                                <input style={{display: 'none'}} onChange={onSelectFile} type={'file'}
                                       accept={".pdf,.xls,.xlsx,.jpg,.jpeg,.png,.gif"}
                                       id={'fileInput'}/>
                            </FooterButton>
                            <FooterButton htmlType={'button'} disabled={!text} onClick={() => onSend(text)}>
                                <SentIcon/>
                            </FooterButton>
                        </FooterButtons>
                    </Footer>
                ) : null
            }
        </Container>
    )
};

const Container = styled.div`
    background-color: #F0F0F3;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;

    &.customer {
        position: fixed;
        left: 0;
        top: 0;
        z-index: 2;
        width: 100%;
        height: 100%;
        border-radius: 0;
    }
`;

const Body = styled.div`
    flex: 1;
    overflow: hidden;
    position: relative;
`;
const Footer = styled.div`
    flex: none;
    background: #ffffff;
    box-shadow: 0px -4px 16px 0px rgba(0, 0, 0, 0.08);
    position: relative;
    padding: 16px;
`;

const FooterInput = styled(Input.TextArea)`
    border-radius: 16px;
    background: #E9EEF6 !important;
    box-shadow: none !important;
    border: none !important;
    padding: 20px 78px 20px 16px;
    font-size: 16px;
`;
const FooterButtons = styled.div`
    position: absolute;
    top: calc(50% - 16px);
    right: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
`;
const FooterButton = styled(ButtonSubmit)`
    display: flex;
    width: 32px !important;
    height: 32px !important;
    margin: 0 !important;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: transparent !important;
    padding: 0 !important;

    &[disabled] {
        background-color: transparent !important;
    }

    svg * {
        fill: ${(props) => hexToRgbA(props.theme.main, 0.6)} !important;
        stroke: ${(props) => props.theme.main} !important;
    }
`;

const CallInviteButton = styled(ButtonCancel)`
    margin: 0 !important;
    position: absolute;
    top: -62px;
    width: 150px;
    left: calc(50% - 75px);
    z-index: 1;
    border: 1px solid ${(props) => props.theme.main} !important;
    color: ${(props) => props.theme.main} !important;
    background-color: white !important;

    svg {
        margin-right: 6px;
        width: 22px;

        path {
            fill: ${(props) => props.theme.main} !important;
            stroke: ${(props) => props.theme.main} !important;
        }
    }
`;

const NoMessages = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    div {
        margin-top: 12px;
        font-size: 16px;
        font-weight: 600;
        color: #494949;
        text-align: center;
        white-space: pre-line;
    }
`;

const SentIcon = (props) => (
    <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M9.51002 4.22965L18.07 8.50965C21.91 10.4296 21.91 13.5696 18.07 15.4896L9.51002 19.7696C3.75002 22.6496 1.40002 20.2896 4.28002 14.5396L5.15002 12.8096C5.37002 12.3696 5.37002 11.6396 5.15002 11.1996L4.28002 9.45965C1.40002 3.70965 3.76002 1.34965 9.51002 4.22965Z"
            stroke="#1D896D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5.43994 12H10.8399" stroke="#1D896D" strokeWidth="1.5" strokeLinecap="round"
              strokeLinejoin="round"/>
    </svg>
)

const IconFile = (props) => (
    <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12.5 14.75H10C9.59 14.75 9.25 14.41 9.25 14C9.25 13.59 9.59 13.25 10 13.25H12.5C15.12 13.25 17.25 11.12 17.25 8.5C17.25 5.88 15.12 3.75 12.5 3.75H7.5C4.88 3.75 2.75 5.88 2.75 8.5C2.75 9.6 3.14 10.67 3.84 11.52C4.1 11.84 4.06 12.31 3.74 12.58C3.42 12.84 2.95 12.8 2.68 12.48C1.76 11.36 1.25 9.95 1.25 8.5C1.25 5.05 4.05 2.25 7.5 2.25H12.5C15.95 2.25 18.75 5.05 18.75 8.5C18.75 11.95 15.95 14.75 12.5 14.75Z"
            fill="#1D896D"/>
        <path
            d="M16.5 21.75H11.5C8.05 21.75 5.25 18.95 5.25 15.5C5.25 12.05 8.05 9.25 11.5 9.25H14C14.41 9.25 14.75 9.59 14.75 10C14.75 10.41 14.41 10.75 14 10.75H11.5C8.88 10.75 6.75 12.88 6.75 15.5C6.75 18.12 8.88 20.25 11.5 20.25H16.5C19.12 20.25 21.25 18.12 21.25 15.5C21.25 14.4 20.86 13.33 20.16 12.48C19.9 12.16 19.94 11.69 20.26 11.42C20.58 11.15 21.05 11.2 21.32 11.52C22.25 12.64 22.76 14.05 22.76 15.5C22.75 18.95 19.95 21.75 16.5 21.75Z"
            fill="#1D896D"/>
    </svg>
)

export const NoMessagesIcon = (props) => (
    <svg {...props} width="218" height="169" viewBox="0 0 218 169" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_2684_19920)">
            <path
                d="M26.0623 155.676H11.2952C11.2952 136.699 -10.1226 118.062 6.02409 116.045C11.7723 115.325 15.8707 118.281 18.7908 122.869C26.0623 134.273 26.0623 155.676 26.0623 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M52.366 155.675H30.3275C30.3275 143.833 22.5502 132.163 16.733 122.211C16.5433 121.894 16.3594 121.571 16.1582 121.254C11.1458 112.575 4.66183 104.542 2.70744 94.4226C2.13262 91.4836 2.0349 88.3083 3.40872 85.6517C4.86876 82.8221 7.84057 81.0414 10.9216 80.2173C47.641 70.7836 55.3149 119.923 52.366 155.675Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M159.57 155.676H141.446C138.491 128.343 139.296 59.8175 178.78 66.5484C209.073 71.7062 160.34 111.187 159.57 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M198.163 155.676H187.391C187.391 127.127 198.312 103.897 213.591 104.202C228.87 104.508 199.382 135.782 198.163 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M42.893 155.676H41.9618C41.8554 150.666 41.3847 145.669 40.5535 140.727C39.5729 134.766 38.1119 128.893 36.1848 123.168C32.3521 111.689 26.4721 101.002 18.8311 91.6284C18.1988 90.8273 18.7563 90.4009 19.4059 91.1097C27.8293 100.11 34.1964 110.841 38.0645 122.557C40.0118 128.397 41.3864 134.413 42.1687 140.52C42.8388 145.542 43.0811 150.613 42.893 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M169.474 81.6408C167.122 84.3524 165.075 87.315 163.369 90.4752C161.632 93.6189 160.145 96.8945 158.92 100.272C157.667 103.644 156.598 107.082 155.718 110.57C154.833 114.028 154.074 117.572 153.465 121.133C152.251 128.248 151.422 135.424 150.982 142.629C150.706 146.974 150.528 151.313 150.488 155.658H149.257C149.096 151.29 149.096 146.916 149.257 142.536C149.507 135.239 150.252 127.966 151.488 120.77C152.114 117.168 152.919 113.596 153.879 110.069C154.827 106.541 155.995 103.077 157.374 99.6957C158.035 97.9668 158.822 96.3302 159.61 94.6705C160.397 93.0108 161.288 91.4145 162.271 89.8701C164.145 86.7163 166.379 83.7921 168.928 81.1567C169.704 80.3038 170.21 80.7649 169.474 81.6408Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M185.511 155.676H164.789C164.789 155.676 159.328 111.879 182.781 111.931C204.124 111.948 189.874 138.999 185.511 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <g>
                <path
                    d="M179.343 122.747C176.262 132.596 174.952 142.917 174.228 153.175C174.182 154.01 174.141 154.852 174.095 155.682H172.296C172.296 154.794 172.365 153.918 172.428 153.048C173.181 142.675 174.912 132.244 178.717 122.505C179.033 121.733 179.596 121.969 179.343 122.747Z"
                    fill="#1D896D" fillOpacity="0.1"/>
            </g>
            <path
                d="M20.4118 155.676H19.4691C19.1242 152.794 18.6874 149.913 18.1471 147.072C17.3442 143.091 16.2746 139.169 14.9453 135.333C14.5889 134.376 14.1866 133.448 13.7957 132.509C13.6002 132.036 13.4278 131.564 13.2209 131.103L12.5426 129.743L11.87 128.377C11.6582 127.916 11.4163 127.469 11.1458 127.04L9.55352 124.441C9.22587 123.911 9.49029 123.692 9.86392 124.234L11.5884 126.775C11.8797 127.198 12.1409 127.641 12.3701 128.1L13.1116 129.449L13.8762 130.815C14.1118 131.27 14.2958 131.748 14.5142 132.215C14.9166 133.16 15.3534 134.094 15.7328 135.045C17.1798 138.895 18.3158 142.855 19.13 146.887C19.6895 149.799 20.1168 152.729 20.4118 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M76.1692 155.676H54.6652C54.6652 128.044 23.4813 100.93 46.9914 97.9677C55.3608 96.9189 61.3274 101.218 65.581 107.897C76.1692 124.477 76.1692 155.676 76.1692 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M67.9436 155.676H66.5697C66.0639 151.486 65.4201 147.291 64.6441 143.142C63.4812 137.346 61.9295 131.635 59.9996 126.049C59.4822 124.66 58.8959 123.306 58.3556 121.94C58.0739 121.255 57.8268 120.563 57.5106 119.889L56.5219 117.912L55.5447 115.918C55.2354 115.248 54.8821 114.599 54.4871 113.976L52.1878 110.196C51.7049 109.418 52.0901 109.101 52.6304 109.89L55.1481 113.59C55.5784 114.204 55.9627 114.849 56.2977 115.521L57.3784 117.486L58.4476 119.468C58.7924 120.125 59.0626 120.822 59.3788 121.502C59.9536 122.88 60.5974 124.234 61.1492 125.628C63.2613 131.233 64.9184 136.999 66.1041 142.871C66.8974 147.128 67.5105 151.396 67.9436 155.676Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path
                d="M208.36 112.512C206.79 114.429 205.379 116.473 204.141 118.621C203.837 119.151 203.509 119.681 203.204 120.223L202.342 121.865C201.738 122.949 201.244 124.09 200.71 125.202C198.68 129.723 197.034 134.408 195.789 139.206C194.455 144.616 193.546 150.123 193.07 155.676H192.248C192.591 150.038 193.45 144.444 194.812 138.964C196.093 134.109 197.825 129.386 199.985 124.856C200.56 123.738 201.072 122.597 201.71 121.525L202.629 119.889C202.951 119.347 203.29 118.823 203.624 118.298C204.94 116.164 206.432 114.143 208.084 112.259C208.613 111.688 208.872 111.919 208.36 112.512Z"
                fill="#1D896D" fillOpacity="0.1"/>
            <path d="M145.82 17.4961H61.5344V147.926H145.82V17.4961Z" fill="#E1F5F0"/>
            <path
                d="M58.5568 9.14546V157.157C58.5568 161.889 62.1091 165.727 66.4835 165.727H140.865C145.239 165.727 148.792 161.889 148.792 157.157V9.14546C148.792 4.4142 145.239 0.576172 140.865 0.576172H66.4835C62.1091 0.576172 58.5568 4.4142 58.5568 9.14546ZM145.814 147.92H61.5343V17.4958H145.814V147.92Z"
                fill="#62B49E"/>
            <path
                d="M148.792 9.14546C148.792 4.4142 145.239 0.576172 140.865 0.576172H66.4835C62.1091 0.576172 58.5568 4.4142 58.5568 9.14546V101.022L61.5343 98.0367V17.4958H141.877L148.775 10.5804L148.792 9.14546Z"
                fill="#62B49E"/>
            <path
                d="M66.4835 0.576172C62.1091 0.576172 58.5568 4.4142 58.5568 9.14546V49.1451L61.5343 46.1657V17.4958H90.1316L107.002 0.576172H66.4835Z"
                fill="#62B49E"/>
            <path
                d="M113.28 11.5835H94.0691C93.9117 11.5835 93.7558 11.552 93.6106 11.4909C93.4654 11.4298 93.3338 11.3403 93.2236 11.2276C93.1133 11.1149 93.0266 10.9813 92.9684 10.8346C92.9103 10.6879 92.8819 10.5311 92.885 10.3733C92.894 10.0644 93.0227 9.7713 93.2438 9.5561C93.4649 9.34089 93.7609 9.22057 94.0691 9.2207H113.28C113.588 9.22057 113.884 9.34089 114.105 9.5561C114.326 9.7713 114.455 10.0644 114.464 10.3733C114.466 10.5309 114.437 10.6874 114.379 10.8337C114.32 10.9801 114.233 11.1134 114.123 11.2259C114.013 11.3385 113.882 11.428 113.737 11.4894C113.592 11.5507 113.437 11.5827 113.28 11.5835Z"
                fill="#404077"/>
            <path
                d="M109.003 157.653C109.003 158.709 108.69 159.742 108.105 160.621C107.519 161.499 106.687 162.184 105.713 162.588C104.74 162.992 103.668 163.098 102.635 162.892C101.601 162.686 100.652 162.177 99.9064 161.43C99.1612 160.683 98.6537 159.731 98.4481 158.695C98.2425 157.659 98.348 156.584 98.7513 155.608C99.1546 154.632 99.8376 153.798 100.714 153.211C101.59 152.624 102.62 152.311 103.674 152.311C104.374 152.31 105.067 152.447 105.714 152.716C106.361 152.984 106.949 153.377 107.444 153.874C107.939 154.37 108.331 154.959 108.599 155.607C108.866 156.256 109.004 156.951 109.003 157.653Z"
                fill="#1D896D"/>
            <path d="M108.968 20.6768H98.3802V23.4544H108.968V20.6768Z" fill="#A2A9F4"/>
            <path d="M108.968 20.6768H98.3802V23.4544H108.968V20.6768Z" fill="white"/>
            <path d="M139.658 20.6768H129.07V23.4544H139.658V20.6768Z" fill="#A2A9F4"/>
            <path d="M139.658 20.6768H129.07V23.4544H139.658V20.6768Z" fill="white"/>
            <path d="M78.0777 20.6768H67.4895V23.4544H78.0777V20.6768Z" fill="#A2A9F4"/>
            <path d="M78.0777 20.6768H67.4895V23.4544H78.0777V20.6768Z" fill="white"/>
            <path d="M146.389 148.496H60.9596V16.9199H146.389V148.496ZM62.1092 147.344H145.24V18.0725H62.1092V147.344Z"
                  fill="#8FC1B4"/>
            <path
                d="M104.749 5.72803C104.749 5.94117 104.686 6.14952 104.568 6.32674C104.45 6.50396 104.282 6.64208 104.086 6.72365C103.889 6.80521 103.673 6.82655 103.465 6.78497C103.256 6.74339 103.065 6.64076 102.914 6.49004C102.764 6.33933 102.661 6.14732 102.62 5.93827C102.579 5.72923 102.6 5.51255 102.681 5.31564C102.763 5.11873 102.9 4.95042 103.077 4.83201C103.254 4.71359 103.462 4.65039 103.674 4.65039C103.959 4.65039 104.233 4.76393 104.434 4.96603C104.636 5.16812 104.749 5.44223 104.749 5.72803Z"
                fill="#404077"/>
            <path
                d="M140.865 166.303H66.4836C61.7989 166.303 57.9821 162.2 57.9821 157.157V9.14557C57.9821 4.10312 61.7989 0 66.4836 0H140.865C145.55 0 149.367 4.10312 149.367 9.14557V157.157C149.367 162.2 145.55 166.303 140.865 166.303ZM66.4836 1.15256C62.4599 1.15256 59.1317 4.73702 59.1317 9.14557V157.157C59.1317 161.566 62.4312 165.15 66.4836 165.15H140.865C144.918 165.15 148.217 161.566 148.217 157.157V9.14557C148.217 4.73702 144.918 1.15256 140.865 1.15256H66.4836Z"
                fill="#8FC1B4"/>
            <path
                d="M82.636 30.3526C82.2025 30.3503 81.7728 30.4338 81.3716 30.5984C80.9704 30.763 80.6055 31.0053 80.2979 31.3116C79.9903 31.6178 79.746 31.9819 79.5791 32.383C79.4121 32.7841 79.3258 33.2143 79.3251 33.6489V46.8515C79.3273 47.2851 79.4148 47.7141 79.5824 48.1138C79.75 48.5136 79.9945 48.8763 80.302 49.1814C80.6094 49.4864 80.9738 49.7277 81.3743 49.8916C81.7747 50.0554 82.2035 50.1386 82.636 50.1363H126.529L131.777 55.4208V50.1363H136.232C137.104 50.1348 137.939 49.787 138.555 49.1691C139.172 48.5513 139.519 47.7137 139.52 46.84V33.6489C139.519 32.7751 139.172 31.9376 138.555 31.3197C137.939 30.7019 137.104 30.3541 136.232 30.3526H82.636Z"
                fill="#F74861"/>
            <path d="M133.973 34.2139H84.8721V36.519H133.973V34.2139Z" fill="#B1B2F3"/>
            <path d="M133.973 34.2139H84.8721V36.519H133.973V34.2139Z" fill="white"/>
            <path d="M133.973 39.0771H84.8721V41.3823H133.973V39.0771Z" fill="#B1B2F3"/>
            <path d="M133.973 39.0771H84.8721V41.3823H133.973V39.0771Z" fill="white"/>
            <path d="M109.014 43.9355H84.8721V46.2407H109.014V43.9355Z" fill="#B1B2F3"/>
            <path d="M109.014 43.9355H84.8721V46.2407H109.014V43.9355Z" fill="white"/>
            <path
                d="M124.736 64.7223C125.608 64.7238 126.445 65.0724 127.061 65.6915C127.677 66.3106 128.024 67.1496 128.024 68.0244V81.2212C128.024 82.096 127.677 82.935 127.061 83.5541C126.445 84.1732 125.608 84.5217 124.736 84.5233H80.8196L75.5715 89.802V84.5233H71.1396C70.2671 84.5217 69.4308 84.1732 68.8144 83.5541C68.1979 82.935 67.8517 82.096 67.8517 81.2212V68.0013C67.8517 67.1265 68.1979 66.2875 68.8144 65.6684C69.4308 65.0493 70.2671 64.7007 71.1396 64.6992L124.736 64.7223Z"
                fill="#3CAFAC"/>
            <path d="M122.477 68.5889H73.3756V70.894H122.477V68.5889Z" fill="#B1B2F3"/>
            <path d="M122.477 68.5889H73.3756V70.894H122.477V68.5889Z" fill="white"/>
            <path d="M122.477 73.4473H73.3756V75.7524H122.477V73.4473Z" fill="#B1B2F3"/>
            <path d="M122.477 73.4473H73.3756V75.7524H122.477V73.4473Z" fill="white"/>
            <path d="M122.477 78.3105H98.3342V80.6157H122.477V78.3105Z" fill="#B1B2F3"/>
            <path d="M122.477 78.3105H98.3342V80.6157H122.477V78.3105Z" fill="white"/>
            <path
                d="M82.636 102.946C81.7634 102.948 80.9271 103.296 80.3107 103.915C79.6942 104.535 79.348 105.374 79.348 106.248V119.445C79.3495 120.319 79.6964 121.157 80.3127 121.774C80.929 122.392 81.7644 122.74 82.636 122.742H126.529L131.777 128.026V122.747H136.232C137.104 122.746 137.939 122.398 138.555 121.78C139.172 121.162 139.519 120.325 139.52 119.451V106.254C139.52 105.379 139.174 104.54 138.557 103.921C137.941 103.302 137.105 102.954 136.232 102.952L82.636 102.946Z"
                fill="#F74861"/>
            <path d="M133.973 106.808H84.8721V109.113H133.973V106.808Z" fill="#B1B2F3"/>
            <path d="M133.973 106.808H84.8721V109.113H133.973V106.808Z" fill="white"/>
            <path d="M133.973 111.672H84.8721V113.977H133.973V111.672Z" fill="#B1B2F3"/>
            <path d="M133.973 111.672H84.8721V113.977H133.973V111.672Z" fill="white"/>
            <path d="M109.014 116.535H84.8721V118.84H109.014V116.535Z" fill="white"/>
            <path
                d="M141.273 67.2218C143.549 64.9398 143.549 61.2399 141.273 58.9579C138.997 56.6759 135.306 56.6759 133.03 58.9579C130.754 61.2399 130.754 64.9398 133.03 67.2218C135.306 69.5038 138.997 69.5038 141.273 67.2218Z"
                fill="#F3DA92"/>
            <path
                d="M137.152 69.7986C135.829 69.7986 134.535 69.4052 133.435 68.6681C132.334 67.9311 131.477 66.8834 130.97 65.6577C130.464 64.432 130.331 63.0833 130.59 61.7821C130.848 60.4809 131.485 59.2856 132.421 58.3475C133.356 57.4094 134.549 56.7705 135.847 56.5117C137.144 56.2529 138.49 56.3857 139.712 56.8934C140.935 57.4011 141.98 58.2609 142.715 59.364C143.45 60.4671 143.843 61.764 143.843 63.0907C143.841 64.8693 143.136 66.5746 141.881 67.8322C140.627 69.0899 138.926 69.7971 137.152 69.7986ZM137.152 58.1117C136.17 58.1117 135.209 58.4037 134.393 58.9508C133.576 59.4979 132.939 60.2755 132.563 61.1853C132.188 62.0951 132.089 63.0962 132.281 64.0621C132.472 65.0279 132.945 65.9151 133.64 66.6114C134.335 67.3078 135.22 67.782 136.183 67.9741C137.146 68.1662 138.145 68.0676 139.052 67.6908C139.96 67.3139 140.736 66.6757 141.281 65.8569C141.827 65.0381 142.118 64.0755 142.118 63.0907C142.115 61.7711 141.591 60.5065 140.66 59.5734C139.73 58.6403 138.468 58.1147 137.152 58.1117Z"
                fill="#FFD150"/>
            <path
                d="M74.9228 101.94C77.199 99.6576 77.199 95.9577 74.9228 93.6757C72.6466 91.3936 68.9561 91.3936 66.6798 93.6757C64.4036 95.9577 64.4036 99.6576 66.6798 101.94C68.9561 104.222 72.6466 104.222 74.9228 101.94Z"
                fill="#2B8986"/>
            <path
                d="M70.8004 104.514C69.4766 104.516 68.1822 104.123 67.081 103.386C65.9798 102.65 65.1213 101.602 64.6142 100.376C64.107 99.1503 63.9741 97.801 64.232 96.4993C64.49 95.1976 65.1274 94.0018 66.0635 93.0633C66.9996 92.1249 68.1923 91.4859 69.4907 91.2272C70.7892 90.9686 72.135 91.1019 73.3578 91.6103C74.5806 92.1188 75.6255 92.9794 76.3603 94.0834C77.0951 95.1874 77.4867 96.4851 77.4855 97.8123C77.4825 99.5889 76.7772 101.292 75.5242 102.548C74.2711 103.804 72.5725 104.511 70.8004 104.514ZM70.8004 92.8332C69.8179 92.8321 68.8571 93.1231 68.0396 93.6696C67.2221 94.216 66.5847 94.9932 66.2079 95.9029C65.8311 96.8126 65.732 97.8139 65.9229 98.7801C66.1139 99.7464 66.5865 100.634 67.2808 101.331C67.9752 102.028 68.8601 102.503 69.8236 102.695C70.7872 102.888 71.7861 102.79 72.6939 102.413C73.6017 102.036 74.3777 101.398 74.9237 100.579C75.4697 99.7602 75.7611 98.7973 75.7611 97.8123C75.7596 96.4932 75.2366 95.2285 74.3068 94.2953C73.377 93.362 72.1161 92.8363 70.8004 92.8332Z"
                fill="#3CAFAC"/>
            <path
                d="M142.817 136.597C143.554 133.456 141.61 130.31 138.476 129.572C135.342 128.834 132.205 130.783 131.469 133.925C130.733 137.066 132.676 140.212 135.81 140.95C138.944 141.688 142.081 139.739 142.817 136.597Z"
                fill="#6574E0"/>
            <path
                d="M142.817 136.597C143.554 133.456 141.61 130.31 138.476 129.572C135.342 128.834 132.205 130.783 131.469 133.925C130.733 137.066 132.676 140.212 135.81 140.95C138.944 141.688 142.081 139.739 142.817 136.597Z"
                fill="#F3DA92"/>
            <path
                d="M137.152 141.978C135.829 141.978 134.535 141.585 133.435 140.848C132.334 140.111 131.477 139.063 130.97 137.837C130.464 136.612 130.331 135.263 130.59 133.962C130.848 132.661 131.485 131.465 132.421 130.527C133.356 129.589 134.549 128.95 135.847 128.691C137.144 128.433 138.49 128.565 139.712 129.073C140.935 129.581 141.98 130.441 142.715 131.544C143.45 132.647 143.843 133.944 143.843 135.27C143.841 137.049 143.136 138.754 141.881 140.012C140.627 141.27 138.926 141.977 137.152 141.978ZM137.152 130.291C136.17 130.291 135.209 130.583 134.393 131.13C133.576 131.678 132.939 132.455 132.563 133.365C132.188 134.275 132.089 135.276 132.281 136.242C132.472 137.208 132.945 138.095 133.64 138.791C134.335 139.487 135.22 139.962 136.183 140.154C137.146 140.346 138.145 140.247 139.052 139.87C139.96 139.494 140.736 138.855 141.281 138.037C141.827 137.218 142.118 136.255 142.118 135.27C142.115 133.951 141.591 132.686 140.66 131.753C139.73 130.82 138.468 130.294 137.152 130.291Z"
                fill="#FFD150"/>
            <path
                d="M166.663 110.069C167.813 112.501 169.928 118.379 169.98 128.943H176.033C175.59 125.566 173.94 115.855 169.503 111.879C169.096 111.521 168.664 111.193 168.209 110.899C167.713 110.588 167.196 110.311 166.663 110.069Z"
                fill="#FFC8AF"/>
            <path
                d="M169.98 128.931C170.005 124.868 169.619 120.814 168.83 116.829C169.543 118.218 171.198 122.016 172.331 128.931H169.98Z"
                fill="#F2A98F"/>
            <path
                d="M138.842 159.365C139.067 160.709 139.549 161.996 140.262 163.157C142.158 166.084 145.768 167.139 152.224 166.499C156.035 166.125 158.978 162.731 162.57 161.077C163.285 160.697 164.031 160.381 164.801 160.131C165.503 160.232 166.195 160.402 166.864 160.639C167.939 160.097 169.014 159.486 170.049 158.91C170.623 158.564 171.198 158.218 171.721 157.867C173.055 156.858 173.446 154.778 173.613 153.573C172.36 152.357 170.982 151.277 169.503 150.352C166.485 148.421 163.524 147.35 162.283 147.35C161.576 147.35 160.558 147.257 159.282 147.35C156.65 147.546 152.959 148.093 148.763 148.785C148.556 148.819 148.349 148.854 148.154 148.9C143.894 149.776 140.21 152.386 138.48 153.781C138.414 155.65 138.535 157.52 138.842 159.365Z"
                fill="#FA4A56"/>
            <path
                d="M133.864 142.445C134.329 143.211 136.485 143.972 139.227 144.669C143.13 145.643 148.229 146.467 151.298 146.934L157.857 146.179L158.874 141.753C154.036 139.915 149.052 138.488 143.975 137.489C143.975 137.489 149.528 125.664 149.953 119.163C150.193 115.612 149.441 112.063 147.78 108.917C145.983 109.392 144.288 110.192 142.779 111.28C142.269 111.685 141.807 112.149 141.405 112.663C135.657 119.607 132.513 140.197 133.864 142.445Z"
                fill="#FFC8AF"/>
            <path
                d="M133.864 142.445C133.844 142.408 133.827 142.369 133.812 142.33C137.396 143.109 141.022 143.686 144.67 144.059C151.384 144.739 156.121 144.918 158.156 144.964L157.874 146.185L151.315 146.952C148.246 146.485 143.147 145.661 139.244 144.687C136.485 143.972 134.329 143.212 133.864 142.445Z"
                fill="#F2A98F"/>
            <path d="M157.874 146.18L159.277 147.309L163.57 128.932H161.852L158.891 141.754L157.874 146.18Z"
                  fill="#56548C"/>
            <path
                d="M163.571 128.932L159.277 147.309H162.283H185.684L185.931 146.249L189.978 128.932H176.038H169.98H163.571Z"
                fill="#112B46"/>
            <path
                d="M151.298 146.952L147.981 147.326L148.148 148.877C148.349 148.827 148.552 148.788 148.757 148.761C154.977 147.736 159.277 147.326 159.277 147.326L157.874 146.174L151.315 146.952H151.298Z"
                fill="#56548C"/>
            <path
                d="M184.074 151.089C182.391 151.261 180.756 151.748 179.251 152.524C181.263 152.768 183.152 153.623 184.666 154.973C185.452 155.736 186.126 156.607 186.667 157.56C187.002 158.14 187.296 158.743 187.546 159.364C191.639 157.814 193.599 155.987 194.444 154.374C194.794 153.768 194.981 153.081 194.986 152.381C194.991 151.68 194.814 150.991 194.473 150.38C192.708 147.816 188.15 146.669 185.942 146.248L185.695 147.308H162.277C163.519 147.308 166.479 148.38 169.497 150.311C174.339 150.824 179.205 151.083 184.074 151.089Z"
                fill="#FA4A56"/>
            <path
                d="M156.926 108.917C156.926 108.917 157 112.628 159.8 112.836C162.599 113.043 163.858 110.45 163.858 110.45C162.708 111.216 161.559 111.781 160.857 111.781C159.225 111.798 156.926 108.917 156.926 108.917Z"
                fill="#F2A98F"/>
            <path
                d="M164.778 160.131C164.009 160.381 163.263 160.695 162.547 161.07L164.668 161.646C165.157 161.439 165.651 161.214 166.14 160.984L166.835 160.644C166.168 160.406 165.479 160.234 164.778 160.131Z"
                fill="#F45D51"/>
            <path
                d="M163.852 110.45C163.852 110.45 162.599 113.043 159.794 112.836C156.989 112.628 156.92 108.917 156.92 108.917C156.92 108.917 159.219 111.798 160.852 111.798C161.536 111.798 162.685 111.222 163.852 110.467C164.323 110.162 164.789 109.828 165.232 109.482C165.764 108.984 166.371 108.574 167.031 108.266C167.666 108.063 168.269 107.768 168.819 107.39C169.151 107.163 169.451 106.892 169.71 106.583C170.376 105.696 169.71 104.52 169.405 103.633C168.87 102.071 168.508 100.509 167.646 99.0799C167.255 98.3245 166.656 97.6967 165.921 97.2704C165.071 96.8528 164.151 96.5931 163.208 96.5039C162.565 96.5018 161.932 96.6645 161.369 96.9765C160.301 97.4378 159.363 98.1564 158.639 99.0684C157.61 100.359 156.736 101.771 155.724 103.102C154.54 104.618 153.425 106.324 152.011 107.615C151.809 107.836 151.579 108.03 151.327 108.191C151.241 108.231 150.718 108.387 150.666 108.323C151.545 109.355 152.362 111.205 153.017 112.899C153.804 114.945 157.449 115.948 160.168 115.781C162.886 115.613 167.014 115.014 163.852 110.45Z"
                fill="#FFC8AF"/>
            <path
                d="M148.148 148.859L147.981 147.309L151.298 146.934C148.229 146.468 143.13 145.644 139.227 144.67C138.81 147.677 138.558 150.705 138.474 153.74C140.204 152.346 143.889 149.735 148.148 148.859ZM143.992 137.489C149.069 138.488 154.053 139.915 158.891 141.754L161.852 128.931H169.98C170.836 128.044 171.371 127.156 171.187 125.243C170.549 118.639 167.554 111.943 166.663 110.058C166.209 109.845 165.726 109.643 165.232 109.482C164.789 109.828 164.323 110.162 163.852 110.467C167.014 115.032 162.806 115.297 160.15 115.458C157.495 115.619 154.621 115.245 153.252 112.68C152.522 111.138 151.648 109.668 150.643 108.289C149.673 108.448 148.713 108.663 147.769 108.934C149.434 112.077 150.193 115.624 149.959 119.175C149.545 125.664 143.992 137.489 143.992 137.489Z"
                fill="#FEC95C"/>
            <path
                d="M165.013 161.451C166.025 161.485 168.75 161.704 170.215 162.407C170.957 162.77 171.773 162.765 172.222 163.231C172.781 163.413 173.276 163.753 173.647 164.211C173.877 164.096 174.147 163.975 174.435 163.848C176.36 163.024 179.642 161.964 186.178 159.814L186.437 159.728C186.822 159.601 187.178 159.474 187.529 159.341C186.88 157.696 185.902 156.202 184.655 154.95C183.141 153.599 181.252 152.744 179.24 152.501C178.29 152.973 177.397 153.553 176.579 154.23C176.165 154.57 175.711 154.933 175.234 155.302C174.182 156.108 172.992 156.956 171.71 157.785C170.144 158.809 168.522 159.744 166.853 160.586C166.611 160.782 165.249 161.358 165.013 161.451Z"
                fill="#FFC8AF"/>
            <path
                d="M165.013 161.451C166.025 161.485 168.75 161.704 170.215 162.407C170.648 162.585 171.091 162.733 171.543 162.851C171.797 162.922 172.03 163.055 172.222 163.237C172.781 163.419 173.276 163.759 173.647 164.217C173.877 164.102 174.141 163.981 174.435 163.854C176.36 163.03 179.642 161.969 186.178 159.82L186.437 159.733C186.822 159.607 187.178 159.48 187.529 159.347C187.278 158.726 186.984 158.123 186.649 157.544C186.109 156.59 185.435 155.719 184.649 154.956C183.135 153.606 181.246 152.751 179.234 152.507C178.287 152.98 177.396 153.56 176.579 154.236C176.165 154.581 175.711 154.939 175.234 155.308C174.182 156.114 172.992 156.961 171.71 157.797C170.142 158.815 168.521 159.748 166.853 160.592C166.606 160.782 165.243 161.358 165.013 161.451Z"
                fill="#FFC8AF"/>
            <path
                d="M171.543 162.851C171.797 162.923 172.03 163.055 172.221 163.238C172.781 163.419 173.276 163.759 173.647 164.217C173.877 164.102 174.141 163.981 174.435 163.854C176.36 163.03 179.642 161.97 186.178 159.82L186.437 159.734C186.822 159.607 187.178 159.48 187.529 159.348C187.278 158.727 186.984 158.124 186.649 157.544C181.597 159.474 175.895 161.422 171.543 162.851Z"
                fill="#F2A98F"/>
            <path
                d="M173.595 153.532C173.457 154.737 173.02 156.817 171.704 157.82C172.986 156.99 174.176 156.143 175.228 155.336C174.72 154.702 174.175 154.1 173.595 153.532Z"
                fill="#F2A98F"/>
            <path
                d="M179.246 152.524C180.75 151.748 182.386 151.262 184.068 151.09C179.201 151.081 174.337 150.822 169.497 150.312C170.975 151.237 172.351 152.316 173.601 153.533C174.181 154.101 174.726 154.703 175.234 155.337C175.711 154.968 176.165 154.605 176.579 154.265C177.398 153.583 178.293 152.999 179.246 152.524Z"
                fill="#F45D51"/>
            <path
                d="M186.195 159.838C179.66 161.976 176.377 163.036 174.452 163.872C180.246 166.292 187.805 164.569 188.736 163.094C189.403 162.027 187.615 160.564 186.437 159.763L186.195 159.838Z"
                fill="#F45D51"/>
            <path
                d="M186.195 159.838C179.66 161.976 176.377 163.036 174.452 163.872C180.246 166.292 187.805 164.569 188.736 163.094C189.403 162.027 187.615 160.564 186.437 159.763L186.195 159.838Z"
                fill="#E83642"/>
            <path
                d="M169.503 150.311C167.991 149.383 164.059 147.309 162.283 147.309L170.33 147.269C170.33 147.269 173.285 150.911 177.09 150.911L176.78 152.219L174.624 151.735L169.503 150.311Z"
                fill="#F45D51"/>
            <path
                d="M164.536 161.07C165.524 161.091 166.509 161.179 167.485 161.335C168.393 161.583 173.417 161.912 173.923 162.442C172.804 162.103 171.62 162.045 170.474 162.275C168.865 162.591 167.219 162.667 165.588 162.499C164.674 162.43 161.214 162.396 159.811 162.373C160.489 161.641 163.019 161.237 164.536 161.07Z"
                fill="#F45D51"/>
            <path
                d="M159.035 163.664C158.616 165.248 160.294 167.859 167.111 168.712C173.929 169.565 175.969 168.504 176.251 166.193C176.384 165.397 176.225 164.578 175.802 163.891C175.379 163.203 174.722 162.692 173.952 162.453C172.833 162.114 171.649 162.057 170.503 162.286C168.894 162.603 167.248 162.679 165.617 162.511C164.703 162.442 163.128 161.894 161.725 161.877C160.495 161.843 159.415 162.223 159.035 163.664Z"
                fill="#F4ABAB"/>
            <g>
                <path
                    d="M138.836 147.994C139.807 147.187 140.888 146.265 141.957 145.291C141.015 145.09 140.089 144.882 139.227 144.669C139.106 145.528 138.968 146.68 138.836 147.994ZM149.895 115.769C150.02 116.896 150.045 118.031 149.97 119.163C149.545 125.663 143.992 137.489 143.992 137.489C145.268 137.719 146.539 137.996 147.786 138.295C149.97 133.455 151.695 123.658 149.895 115.769Z"
                    fill="#D1D3D4"/>
            </g>
            <path d="M174.463 139.402H176.906L177.291 137.702H174.854L174.463 139.402Z" fill="#56548C"/>
            <path
                d="M142.118 108.762C141.772 109.151 141.512 109.608 141.355 110.105C141.197 110.601 141.146 111.125 141.204 111.643C141.23 111.984 141.288 112.322 141.377 112.652C141.788 112.138 142.259 111.676 142.779 111.274C144.289 110.187 145.984 109.385 147.78 108.906C148.725 108.635 149.684 108.419 150.654 108.26C151.186 108.136 151.738 108.136 152.269 108.26C152.852 108.518 153.503 108.575 154.12 108.422C154.776 108.157 154.695 106.537 155.408 106.451C156.126 106.421 156.818 106.168 157.385 105.725C158.34 104.86 158.196 103.42 159.006 102.434C159.581 101.766 160.495 101.541 161.306 101.241C161.993 101.062 162.601 100.656 163.03 100.089C163.22 99.7891 163.375 99.4318 163.559 99.1148C163.473 99.2589 163.846 100.094 163.898 100.267C164.046 100.722 164.238 101.16 164.473 101.576C164.776 102.125 165.164 102.623 165.623 103.051C166.125 103.433 166.601 103.849 167.048 104.296C167.588 104.924 168.1 106.14 167.744 106.964C167.555 107.346 167.298 107.69 166.985 107.978C166.448 108.523 165.872 109.028 165.26 109.488C165.755 109.667 166.238 109.868 166.692 110.064C167.225 110.306 167.742 110.583 168.238 110.894C168.22 110.662 168.238 110.429 168.29 110.202C168.371 109.826 168.535 109.473 168.768 109.167C169.002 108.861 169.3 108.611 169.64 108.433C170.127 108.259 170.591 108.025 171.02 107.736C171.238 107.482 171.394 107.18 171.474 106.855C171.555 106.53 171.559 106.19 171.486 105.863C171.327 105.212 171.208 104.552 171.129 103.886C171.078 102.647 171.744 101.506 172.095 100.313C172.498 98.952 172.491 97.5014 172.074 96.144C171.657 94.7866 170.85 93.583 169.753 92.6845C168.656 91.786 167.318 91.2327 165.908 91.0941C164.498 90.9555 163.079 91.2378 161.829 91.9055C161.06 91.2888 160.093 90.9758 159.11 91.0257C158.127 91.0756 157.197 91.485 156.494 92.1764C155.31 93.329 154.885 95.1327 153.557 96.1124C152.327 97.0114 150.482 97.069 149.643 98.3484C148.861 99.5009 149.298 101.333 148.263 102.302C147.372 103.131 145.791 102.947 145.09 103.933C144.515 104.745 144.877 105.926 144.423 106.814C143.969 107.701 142.831 108.053 142.118 108.762Z"
                fill="#0A1F2D"/>
            <path
                d="M140.256 163.156C142.153 166.084 145.763 167.139 152.218 166.499C156.029 166.124 158.972 162.73 162.565 161.076C163.279 160.696 164.025 160.38 164.795 160.131C165.498 160.232 166.189 160.402 166.858 160.638C167.933 160.096 169.008 159.486 170.043 158.909C167.577 158.235 164.186 157.48 162.57 157.849C159.851 158.466 152.068 162.845 146.01 163.329C144.091 163.483 142.162 163.425 140.256 163.156Z"
                fill="#E83642"/>
            <path
                d="M186.667 157.561C187.002 158.141 187.295 158.743 187.546 159.365C191.639 157.814 193.599 155.988 194.444 154.374C192.329 155.354 189.61 156.454 186.667 157.561Z"
                fill="#F45D51"/>
            <path d="M12.9104 23.501L19.1299 39.4467H26.5968L20.383 23.501H12.9104Z" fill="#112B46"/>
            <path d="M21.4924 22.665H13.9048L12.9104 23.5006H20.383L21.4924 22.665Z" fill="#61619B"/>
            <path d="M20.3831 23.5006L26.5968 39.4463L27.3958 38.1785L21.4925 22.665L20.3831 23.5006Z" fill="#56548C"/>
            <path
                d="M34.5524 50.8567C34.5524 54.7063 34.5524 58.5789 34.5122 62.1979C26.2233 66.3875 22.3547 55.2537 18.8771 49.1221C14.3245 41.0945 12.6805 35.0551 12.8127 33.4242C12.945 31.7933 14.5717 28.8428 15.1523 28.3991C15.7328 27.9553 19.5266 28.3991 20.567 28.716C21.6075 29.033 22.1363 31.851 22.3317 32.969C22.4065 33.3896 21.8546 33.7757 21.1361 34.1215C20.2303 34.4906 19.2935 34.778 18.3368 34.9802C19.4864 36.4439 18.3368 39.6884 18.3368 39.6884L28.5053 49.877C28.5053 49.877 28.8732 48.1482 29.4825 45.3129L34.5869 47.618C34.5524 48.6784 34.5466 49.756 34.5524 50.8567Z"
                fill="#FFC8AF"/>
            <path
                d="M18.3367 34.9576C19.4864 36.4213 18.3367 39.6658 18.3367 39.6658C18.3367 39.6658 18.2505 38.1098 18.1413 36.7844C18.0321 35.459 16.4168 34.6176 16.9514 34.3237C17.348 34.1047 19.8543 34.1047 21.1361 34.122C20.2296 34.4832 19.2927 34.7629 18.3367 34.9576Z"
                fill="#F2A98F"/>
            <path
                d="M22.9239 165.784C22.7641 165.95 22.654 166.157 22.6061 166.382C22.5582 166.607 22.5745 166.841 22.653 167.058C22.7316 167.274 22.8692 167.464 23.0503 167.606C23.2313 167.747 23.4484 167.835 23.6769 167.859C27.5971 168.216 34.6329 166.862 36.7712 166.182C37.0801 166.087 37.351 165.896 37.5451 165.638C37.7393 165.379 37.8467 165.065 37.8519 164.741L37.4783 159.318C35.2595 160.471 33.3971 160.984 31.0116 160.713C28.6031 161.889 24.3552 164.326 22.9239 165.784Z"
                fill="#112B46"/>
            <path
                d="M29.5974 29.7941C29.5974 31.0792 31.6955 33.6206 31.6955 33.6206C30.4309 35.7529 30.5459 37.2973 31.0517 38.2309C31.4368 38.9512 32.0461 39.2855 32.3623 39.199C33.0636 39.0204 32.9371 37.5681 33.8108 37.8621C34.6846 38.156 33.989 39.9424 34.2132 41.2045C34.2994 41.6078 34.5229 41.9685 34.8455 42.2245C35.3258 42.6231 35.8726 42.9333 36.4607 43.1408C41.088 56.7179 58.0337 69.4134 61.5573 69.9609C62.6158 70.1237 63.6983 69.9333 64.6384 69.4192C67.6389 67.7825 70.4728 62.3713 68.938 48.1544C65.2067 49.7183 61.1185 50.2246 57.1197 49.6182C58.1774 53.8999 58.6487 56.8908 58.6487 56.8908C51.9879 51.893 45.6466 46.4806 39.6625 40.6858C39.2411 38.3382 38.4658 36.0686 37.3632 33.9549C37.3632 33.9549 36.8574 33.5515 36.1044 32.9752C35.3513 32.3989 34.173 31.5287 33.0923 30.8141C31.4943 29.7538 29.994 28.9297 29.7239 29.385C29.654 29.5075 29.6202 29.6474 29.6262 29.7884L29.5974 29.7941Z"
                fill="#FFC8AF"/>
            <path
                d="M29.5974 29.7941C29.5974 31.0792 31.6955 33.6206 31.6955 33.6206C30.4309 35.7529 30.5459 37.2973 31.0517 38.2309C31.4368 38.9512 32.0461 39.2855 32.3623 39.199C33.0636 39.0204 32.9371 37.5681 33.8108 37.8621C34.6846 38.156 33.989 39.9424 34.2132 41.2045C34.2994 41.6078 34.5229 41.9685 34.8455 42.2245C35.3258 42.6231 35.8726 42.9333 36.4607 43.1408C41.088 56.7179 58.0337 69.4134 61.5573 69.9609C62.6158 70.1237 63.6983 69.9333 64.6384 69.4192C67.6389 67.7825 70.4728 62.3713 68.938 48.1544C65.2072 49.7203 61.1185 50.2267 57.1197 49.6182C58.1774 53.8999 58.6487 56.8908 58.6487 56.8908C51.9879 51.893 45.6466 46.4806 39.6625 40.6858C39.2411 38.3382 38.4658 36.0686 37.3632 33.9549C37.3632 33.9549 36.8574 33.5515 36.1044 32.9752C35.3513 32.3989 34.173 31.5287 33.0923 30.8141C31.4943 29.7538 29.994 28.9297 29.7239 29.385C29.654 29.5075 29.6202 29.6474 29.6262 29.7884L29.5974 29.7941Z"
                fill="#FFC8AF"/>
            <path
                d="M34.2133 41.1985C33.9891 39.9364 34.7019 38.1557 33.8109 37.8561C32.92 37.5564 33.0637 39.0086 32.3624 39.193C32.0462 39.2795 31.4427 38.9452 31.0575 38.2249C30.4655 40.9161 29.8907 43.3711 29.4825 45.3131L34.5869 47.6182C34.6214 45.7396 34.7191 43.9301 34.8456 42.2127C34.5259 41.9566 34.3029 41.5988 34.2133 41.1985ZM33.0637 30.8254C34.1443 31.5343 35.2537 32.3583 36.0757 32.9865C37.1219 27.8 38.5647 24.4345 39.9615 23.8467C37.2081 25.0165 34.8456 26.3881 33.0637 30.8139V30.8254Z"
                fill="#1D896D"/>
            <path d="M37.0356 89.5889L38.8117 89.7099L36.1044 112.992C35.4938 105.181 35.8064 97.3257 37.0356 89.5889Z"
                  fill="#4052D3"/>
            <path
                d="M61.5574 69.955C58.0338 69.3787 41.0881 56.7006 36.4608 43.1349C35.8727 42.9275 35.3258 42.6173 34.8456 42.2187C34.7191 43.9475 34.6214 45.7455 34.5869 47.6242C34.5524 48.6788 34.5467 49.7564 34.5524 50.8571C34.5524 54.7066 34.5524 58.5793 34.5122 62.1983C34.3915 72.433 34.0753 80.6393 34.0753 80.6393C44.1691 83.4515 54.5963 83.3824 59.4363 83.1403C61.201 83.0539 62.2242 82.9444 62.2242 82.9444C62.2242 82.9444 63.6095 75.7927 64.6384 69.396C63.7004 69.9162 62.6178 70.1126 61.5574 69.955ZM39.6626 40.6857C45.6411 46.48 51.9766 51.8923 58.6316 56.8908C58.6316 56.8908 58.1602 53.8999 57.1026 49.6181C56.2036 45.9488 55.0806 42.3383 53.7399 38.8071C50.8658 31.2405 53.4525 24.1004 59.4881 27.656C57.0477 25.3935 54.3019 23.4866 51.3314 21.9912C51.0785 23.1034 50.1817 24.9418 46.9685 25.9099C44.6692 26.6015 43.1747 25.8753 42.232 24.9302C41.8236 24.5096 41.4806 24.0298 41.2146 23.5068C40.7915 23.6528 40.357 23.7627 39.9155 23.8353C38.5187 24.4116 37.0759 27.7886 36.0297 32.9751C36.7827 33.5514 37.2886 33.9548 37.2886 33.9548C38.4171 36.0642 39.2177 38.334 39.6626 40.6857Z"
                fill="#1D896D"/>
            <path
                d="M41.2605 23.5066C41.5265 24.0295 41.8695 24.5094 42.2779 24.93C45.152 23.3683 44.8934 21.0459 44.8934 21.0459C43.957 22.214 42.6909 23.0716 41.2605 23.5066Z"
                fill="#F2A98F"/>
            <path
                d="M47.1351 126.845L42.1802 144.398C43.8334 147.977 45.6794 151.463 47.7099 154.84C47.7099 154.84 52.1303 154.224 54.671 150.409C51.1553 142.913 48.6223 134.993 47.1351 126.845Z"
                fill="#3B397F"/>
            <path
                d="M39.9615 23.8353C40.403 23.7627 40.8375 23.6528 41.2606 23.5068C42.6909 23.0718 43.9571 22.2141 44.8934 21.0461C44.8934 21.0461 45.1693 23.3512 42.278 24.9302C43.2207 25.8753 44.7152 26.6014 47.0145 25.9099C50.2105 24.9417 51.1244 23.1034 51.3774 21.9912C51.2845 21.9443 51.1943 21.8924 51.1072 21.8356C48.5723 20.2911 48.6872 18.1877 48.6872 18.1877C47.8988 17.7476 47.1817 17.1899 46.5604 16.5338C46.5876 16.1732 46.4729 15.8163 46.2409 15.5394C46.0088 15.2626 45.6778 15.0879 45.3188 15.0528C45.5973 14.8562 45.8354 14.6078 46.0201 14.3209C46.4799 13.5141 46.0718 12.4768 45.4452 11.8083C45.1038 11.4137 44.644 11.1407 44.1347 11.0303C43.8803 10.9807 43.617 11.0092 43.3791 11.112C43.1412 11.2148 42.9397 11.3872 42.8011 11.6066C42.2607 12.6093 44.0254 14.7761 42.9793 15.1911C42.3355 15.4389 41.8296 13.4622 41.0134 13.0473C40.1378 12.7053 39.303 12.2665 38.5244 11.7391C37.9094 11.2551 37.4265 10.5866 36.7195 10.2235C35.7595 9.75098 34.6157 9.99878 33.6327 10.4022C32.7997 10.8294 31.9326 11.186 31.0403 11.4683C32.3221 16.5165 36.9322 24.3482 39.9615 23.8353Z"
                fill="#FFC8AF"/>
            <path
                d="M44.8934 16.3378C44.9799 16.4954 45.105 16.6283 45.2568 16.7241C45.4086 16.8199 45.5823 16.8753 45.7614 16.8853C45.9479 16.8741 46.1291 16.8188 46.2902 16.7239C46.3514 16.7048 46.4081 16.6734 46.4569 16.6317C46.5202 16.5568 46.5087 16.5914 46.4569 16.505C46.4052 16.4185 45.3073 15.0815 45.3073 15.0527C45.0831 15.2256 44.9049 15.3121 44.8187 15.5887C44.7351 15.8076 44.7351 16.0498 44.8187 16.2687L44.8934 16.3378Z"
                fill="#F2A98F"/>
            <path
                d="M47.7099 154.841C47.7099 154.841 44.8761 166.66 47.2961 165.732C50.5725 164.482 54.1307 156.714 55.3435 153.798C55.4872 153.464 55.556 153.102 55.5451 152.739C55.5342 152.376 55.4438 152.019 55.2803 151.694C55.1078 151.354 54.8952 150.922 54.6537 150.409C52.1073 154.224 47.7099 154.841 47.7099 154.841Z"
                fill="#112B46"/>
            <path
                d="M42.9965 15.2078C44.0427 14.7928 42.278 12.626 42.8183 11.6233C42.957 11.4039 43.1584 11.2315 43.3963 11.1287C43.6343 11.0259 43.8976 10.9974 44.1519 11.047C44.6612 11.1574 45.1211 11.4304 45.4625 11.825C46.0948 12.4935 46.5029 13.5538 46.0373 14.3376C45.8526 14.6244 45.6145 14.8729 45.336 15.0695C45.7096 15.5854 46.1187 16.0745 46.5604 16.5332C47.1747 17.1875 47.884 17.745 48.6642 18.1871C48.8654 15.1732 49.5264 12.0094 49.1586 9.00698C49.0719 8.02821 48.8313 7.06934 48.4458 6.16592C48.0704 5.25253 47.3896 4.49864 46.5201 4.03368C45.5027 3.54384 44.2956 3.60723 43.2264 3.23841C42.5033 2.92825 41.8006 2.57215 41.1226 2.1723C40.4281 1.76883 39.6132 1.62587 38.8233 1.7689C37.6737 2.06856 36.9207 3.27299 35.7538 3.42282C35.084 3.42616 34.4194 3.30502 33.7937 3.06553C33.4806 2.94924 33.1425 2.91785 32.8135 2.97451C32.4844 3.03118 32.1762 3.17389 31.9198 3.38825C31.5691 3.78588 31.5404 4.36792 31.2127 4.77132C30.6379 5.5032 29.4883 5.31302 28.5973 5.54354C28.0864 5.69444 27.6286 5.98719 27.2766 6.38796C26.9247 6.78872 26.693 7.28114 26.6084 7.80832C26.5375 8.33535 26.598 8.87181 26.7846 9.36964C26.9712 9.86748 27.278 10.3111 27.6776 10.6609C28.0849 11.0133 28.5635 11.273 29.0805 11.4222C29.5975 11.5714 30.1406 11.6066 30.6724 11.5253L31.023 11.4677C31.9265 11.1928 32.8053 10.8419 33.65 10.4189C34.6329 10.0155 35.7768 9.76767 36.7367 10.2402C37.4438 10.586 37.9266 11.2718 38.5417 11.7558C39.3203 12.2832 40.1551 12.722 41.0306 13.064C41.8756 13.4732 42.3527 15.4556 42.9965 15.2078Z"
                fill="#112B46"/>
            <path
                d="M59.534 27.6611C53.4696 24.1055 50.86 31.2456 53.7858 38.8122C55.1165 42.3422 56.2299 45.9507 57.1197 49.6174C61.1195 50.2244 65.2088 49.714 68.938 48.1421V48.1018C67.8286 37.8959 63.6267 31.5741 59.534 27.6611Z"
                fill="#1D896D"/>
            <path
                d="M30.5516 160.581C32.846 161.287 35.2988 161.287 37.5932 160.581L42.1572 144.399L47.1352 126.845L59.4363 83.1573C54.5963 83.3993 44.1691 83.4685 34.0753 80.6562C34.0753 80.6562 29.2698 98.8437 34.903 123.249L36.4435 110.07L34.903 123.232L30.5516 160.581ZM36.1044 112.991C35.4939 105.181 35.8064 97.3255 37.0356 89.5886L38.8118 89.7096L36.1044 112.991Z"
                fill="#4C468E"/>
            <g>
                <path
                    d="M61.5573 69.9543C62.6158 70.1171 63.6983 69.9268 64.6383 69.4126C64.3452 71.2509 64.0175 73.1527 63.7071 74.9334C61.6205 75.3656 59.6432 75.2849 58.1544 74.2303C54.4066 71.5794 47.9341 61.8576 44.1805 55.9219C50.8427 63.7823 59.2063 69.5855 61.5573 69.9543Z"
                    fill="#F1F2F2"/>
            </g>
            <path
                d="M19.0668 25.1487C19.1189 25.2738 19.2183 25.3732 19.3433 25.425C19.4682 25.4768 19.6085 25.477 19.7336 25.4254C19.8197 25.3399 19.8767 25.2294 19.8964 25.1095C19.9161 24.9897 19.8976 24.8667 19.8434 24.758C19.7892 24.6494 19.7022 24.5607 19.5947 24.5046C19.4872 24.4485 19.3648 24.4279 19.245 24.4457C19.1872 24.4801 19.1368 24.5256 19.0966 24.5796C19.0564 24.6335 19.0272 24.6949 19.0106 24.7602C18.9941 24.8255 18.9905 24.8934 19.0002 24.9601C19.0098 25.0267 19.0324 25.0909 19.0668 25.1487Z"
                fill="#61619B"/>
            <path
                d="M19.1702 25.097C19.1847 25.1482 19.2103 25.1957 19.2452 25.2359C19.28 25.2762 19.3233 25.3082 19.3719 25.3298C19.4205 25.3514 19.4732 25.362 19.5264 25.3609C19.5795 25.3598 19.6318 25.3469 19.6794 25.3232C19.727 25.2996 19.7689 25.2657 19.802 25.224C19.8351 25.1823 19.8586 25.1338 19.8709 25.0819C19.8832 25.0301 19.884 24.9762 19.8732 24.924C19.8623 24.8718 19.8402 24.8226 19.8083 24.78C19.7888 24.733 19.7602 24.6903 19.7242 24.6544C19.6882 24.6184 19.6454 24.59 19.5984 24.5707C19.5514 24.5515 19.501 24.5417 19.4502 24.542C19.3994 24.5423 19.3492 24.5527 19.3024 24.5726C19.2163 24.6252 19.1541 24.7096 19.1294 24.8077C19.1047 24.9057 19.1193 25.0096 19.1702 25.097Z"
                fill="#BEE1F4"/>
            <path
                d="M33.1096 60.3823C34.2592 58.0772 34.4719 49.8191 34.3512 48.9316C34.2765 48.3899 33.6499 47.4391 31.9312 46.4248L34.5869 47.6235C34.5524 48.6781 34.5466 49.7557 34.5524 50.8564C34.5524 54.706 34.5524 58.5786 34.5121 62.1976C32.0692 63.4366 30.0113 63.3502 28.2294 62.4223C30.316 62.1169 32.529 61.5291 33.1096 60.3823Z"
                fill="#F2A98F"/>
            <path
                d="M29.5974 29.7939C29.5974 31.0791 31.6955 33.6204 31.6955 33.6204C32.2703 32.8252 32.6095 32.4218 32.6095 32.4218L29.5974 29.7939Z"
                fill="#F2A98F"/>
            <path
                d="M58.6315 56.89L58.4073 50.8621L60.7066 50.7007C62.073 50.5027 63.3975 50.0802 64.6268 49.4502C62.1386 49.951 59.5816 50.0076 57.0737 49.6173C58.1774 53.8991 58.6315 56.89 58.6315 56.89Z"
                fill="#F2A98F"/>
            <path
                d="M43.7667 13.2889C43.623 13.0065 43.3758 12.5167 43.5138 12.194C43.7609 11.6177 44.6634 12.0038 44.9336 12.4015C45.2037 12.7991 45.5544 13.554 45.1405 13.9113C45.0691 13.9646 44.9824 13.9934 44.8933 13.9934C44.8043 13.9934 44.7176 13.9646 44.6462 13.9113C44.4852 13.7845 44.5657 13.652 44.5369 13.4733C44.4795 13.1679 43.9736 13.0815 43.7667 13.2889Z"
                fill="#F2A98F"/>
        </g>
        <defs>
            <clipPath id="clip0_2684_19920">
                <rect width="218" height="169" fill="white"/>
            </clipPath>
        </defs>
    </svg>
)
